// The Bluffmania host. Plays pre-recorded host lines and question readings when they exist
// (public/audio/manifest.json, generated in the chosen narrator voice), otherwise falls back to
// the browser's speech synthesis when it has a voice for the language. It never reads Georgian
// with an English voice — if no suitable voice exists it simply stays silent.

import type { Lang } from "@shared/types";
import { dictFor } from "../i18n";
import { asset } from "./asset";
import { prefs } from "./prefs";
import { audioContext, duck, markPlayed, play } from "./sfx";

export type HostKey =
  | "welcome" | "act0" | "act1" | "act2" | "write" | "hurry" | "timeup" | "vote" | "lie"
  | "fooledMany" | "truth" | "nobody" | "everyone" | "scores" | "winner" | "outro"
  | "qnum" | "joke" | "welcomeBack" | "prevWinner" | "prevLoser" | "prevWinnerName" | "prevLoserName";

interface Manifest {
  voice: string;
  /** "ka/welcome" → ["host/ka/welcome-1.mp3", …] */
  lines: Record<string, string[]>;
  /**
   * "ka" → { "b001": "q/ka/b001.mp3" } — one whole reading in which the host voices the blank
   * himself ("…what?"), or { "b001": ["q/ka/b001-a.mp3", "q/ka/b001-b.mp3"] } — two halves with a
   * bleep in between.
   */
  questions: Record<string, Record<string, string | string[]>>;
  /**
   * "ka" → { "მარიამი": "host/ka/name-mariam.mp3", "mariam": "host/ka/name-mariam.mp3", … }: common first names
   * recorded in the form the host's "prev…Name" lines start with (Georgian: the dative, "მარიამს, …").
   */
  names?: Record<string, Record<string, string>>;
}

/** The lookup key for a player's name: its first word, lower-case, letters only. */
export function nameKey(name: string): string {
  const first = name.normalize("NFC").trim().split(/[\s,.;:!?()\[\]{}"'«»„“-]+/)[0] ?? "";
  return first.toLowerCase().replace(/[^\p{L}]/gu, "");
}

function nameClip(name: string, lang: Lang): string | null {
  const map = manifest?.names?.[lang];
  if (!map) return null;
  const k = nameKey(name);
  if (!k) return null;
  // Georgian nominative endings vary ("მარიამ" / "მარიამი"), so try both
  return map[k] ?? (k.endsWith("ი") ? map[k.slice(0, -1)] : map[`${k}ი`]) ?? null;
}

let manifest: Manifest | null = null;
let manifestLoad: Promise<void> | null = null;
const buffers = new Map<string, Promise<AudioBuffer | null>>();

function loadManifest(): Promise<void> {
  if (!manifestLoad) {
    manifestLoad = fetch(asset("audio/manifest.json"))
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        manifest = m;
      })
      .catch(() => {
        manifest = null;
      });
  }
  return manifestLoad;
}

function getBuffer(path: string): Promise<AudioBuffer | null> {
  let p = buffers.get(path);
  if (!p) {
    p = (async () => {
      const c = audioContext();
      if (!c) return null;
      try {
        const r = await fetch(asset(`audio/${path}`));
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await c.decodeAudioData(await r.arrayBuffer());
      } catch {
        buffers.delete(path); // a network hiccup must not mute this clip for the rest of the game
        return null;
      }
    })();
    buffers.set(path, p);
  }
  return p;
}

type Step = { file: string } | { speak: string; lang: Lang } | { sfx: "bleep" } | { pause: number };

class Narrator {
  private gen = 0;
  private chain: Promise<void> = Promise.resolve();
  private source: AudioBufferSourceNode | null = null;
  /** Automatic mode: speak on this device? (host device / one-device games) */
  autoOn = false;

  enabled(): boolean {
    const p = prefs.get();
    return p.narrator ?? this.autoOn;
  }

  /** Stop whatever is being said and drop the queue. */
  cancel() {
    this.gen++;
    try {
      this.source?.stop();
    } catch {
      /* already stopped */
    }
    this.source = null;
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* unsupported */
    }
    this.chain = Promise.resolve();
  }

  line(key: HostKey, lang: Lang, seed?: number): Promise<void> {
    if (!this.enabled()) return Promise.resolve();
    return this.enqueue(async () => {
      await loadManifest();
      const files = manifest?.lines[`${lang}/${key}`];
      const texts = dictFor(lang).host[key];
      const i = seed === undefined ? Math.floor(Math.random() * Math.max(1, files?.length ?? texts.length)) : seed;
      if (files && files.length) return [{ file: files[i % files.length] }];
      return [{ speak: texts[i % texts.length], lang }];
    });
  }

  /** How many versions of a line this device can say: its recordings, or else its texts. */
  count(key: HostKey, lang: Lang): number {
    return manifest?.lines[`${lang}/${key}`]?.length || dictFor(lang).host[key].length;
  }

  /** "Question two!" — `n` counts from 1. */
  questionNumber(n: number, lang: Lang): Promise<void> {
    return this.line("qnum", lang, n - 1);
  }

  /**
   * A word about the previous game on a replay: the winner's crown is in danger, or let's cheer up the
   * one who came last. Common names are said by the host himself; any other name gets the version
   * without a name.
   */
  prevGame(kind: "winner" | "loser", name: string | null, lang: Lang): Promise<void> {
    if (!this.enabled()) return Promise.resolve();
    return this.enqueue(async () => {
      await loadManifest();
      const clip = name ? nameClip(name, lang) : null;
      const key: HostKey = clip ? (kind === "winner" ? "prevWinnerName" : "prevLoserName") : kind === "winner" ? "prevWinner" : "prevLoser";
      const files = manifest?.lines[`${lang}/${key}`];
      const texts = dictFor(lang).host[key];
      const i = Math.floor(Math.random() * Math.max(1, files?.length ?? texts.length));
      const line: Step = files?.length ? { file: files[i % files.length] } : { speak: texts[i % texts.length], lang };
      return clip ? [{ file: clip }, line] : [line];
    });
  }

  /**
   * Read a Bluffmania question. A recorded reading is played as it is (the host voices the blank
   * himself); a reading recorded in two halves, or the browser voice, gets a short bleep for "____".
   * `force` reads it even on a device where the host voice is off (the "read it again" button).
   */
  question(id: string, text: string, lang: Lang, force = false): Promise<void> {
    if (!force && !this.enabled()) return Promise.resolve();
    return this.enqueue(async () => {
      await loadManifest();
      const rec = manifest?.questions[lang]?.[id];
      if (typeof rec === "string" && rec) return [{ file: rec }];
      if (Array.isArray(rec) && rec.length) {
        const steps: Step[] = [{ file: rec[0] }];
        if (text.includes("____")) steps.push({ sfx: "bleep" });
        if (rec[1]) steps.push({ file: rec[1] });
        return steps;
      }
      await voicesReady();
      if (!pickVoice(lang)) return []; // no recording and no voice for the language: stay quiet (no lone bleep)
      const [a, b] = text.split("____");
      const steps: Step[] = [];
      if (a?.trim()) steps.push({ speak: a.trim(), lang });
      if (text.includes("____")) steps.push({ sfx: "bleep" });
      if (b?.trim()) steps.push({ speak: b.trim(), lang });
      return steps;
    });
  }

  /** Whether this device can read the question aloud at all: a recording of it, or a system voice for the language. */
  async canRead(id: string, lang: Lang): Promise<boolean> {
    await loadManifest();
    const rec = manifest?.questions[lang]?.[id];
    if ((typeof rec === "string" && rec) || (Array.isArray(rec) && rec.length)) return true;
    await voicesReady();
    return !!pickVoice(lang);
  }

  /** Stay quiet for a moment between two queued lines. */
  pause(ms: number): Promise<void> {
    if (!this.enabled()) return Promise.resolve();
    return this.enqueue(async () => [{ pause: ms }]);
  }

  /** Start downloading a question's recording early (during the intro), so it starts without a gap. */
  prefetchQuestion(id: string, lang: Lang) {
    if (!this.enabled()) return;
    void loadManifest().then(() => {
      const rec = manifest?.questions[lang]?.[id];
      for (const file of typeof rec === "string" ? [rec] : (rec ?? [])) void getBuffer(file);
    });
  }

  private enqueue(build: () => Promise<Step[]>): Promise<void> {
    const gen = this.gen;
    const run = async () => {
      if (gen !== this.gen) return;
      const steps = await build();
      for (const s of steps) {
        if (gen !== this.gen) return;
        await this.step(s, gen);
      }
    };
    this.chain = this.chain.then(run, run);
    return this.chain;
  }

  private async step(s: Step, gen: number): Promise<void> {
    if ("pause" in s) return new Promise((r) => setTimeout(r, s.pause));
    if ("sfx" in s) {
      play(s.sfx, { force: false });
      return new Promise((r) => setTimeout(r, 480));
    }
    if ("file" in s) {
      const buf = await getBuffer(s.file);
      if (!buf || gen !== this.gen) return;
      const c = audioContext();
      if (!c) return;
      const src = c.createBufferSource();
      src.buffer = buf;
      const g = c.createGain();
      g.gain.value = Math.min(1, prefs.get().volume * 1.15);
      src.connect(g).connect(c.destination);
      this.source = src;
      duck(buf.duration * 1000 + 200);
      markPlayed(buf.duration * 1000 + 250);
      await new Promise<void>((resolve) => {
        src.onended = () => resolve();
        src.start();
      });
      this.source = null;
      return;
    }
    await speakBrowser(s.speak, s.lang);
  }
}

const LOCALE: Record<Lang, string> = { ka: "ka-GE", en: "en-US", ru: "ru-RU" };

function pickVoice(lang: Lang): SpeechSynthesisVoice | null {
  const synth = window.speechSynthesis;
  if (!synth) return null;
  const voices = synth.getVoices().filter((v) => v.lang.toLowerCase().startsWith(lang));
  if (!voices.length) return null;
  const score = (v: SpeechSynthesisVoice) =>
    (/natural|online|neural|premium|enhanced/i.test(v.name) ? 4 : 0) +
    (/google|microsoft|apple/i.test(v.name) ? 1 : 0) +
    (/giorgi|dmitry|guy|davis|daniel|male/i.test(v.name) ? 2 : 0) +
    (v.localService ? 0 : 1);
  return voices.sort((a, b) => score(b) - score(a))[0];
}

/** The browser lists its voices a moment after the page loads; wait for them (at most 0.8 s). */
function voicesReady(): Promise<void> {
  const synth = typeof window === "undefined" ? undefined : window.speechSynthesis;
  if (!synth || synth.getVoices().length) return Promise.resolve();
  return new Promise((resolve) => {
    synth.addEventListener("voiceschanged", () => resolve(), { once: true });
    setTimeout(resolve, 800);
  });
}

function speakBrowser(text: string, lang: Lang): Promise<void> {
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    if (!synth) return resolve();
    const go = () => {
      const voice = pickVoice(lang);
      if (!voice) return resolve(); // never read Georgian with a foreign voice
      const u = new SpeechSynthesisUtterance(text);
      u.voice = voice;
      u.lang = LOCALE[lang];
      u.rate = lang === "ka" ? 0.98 : 1.04;
      u.pitch = 0.92;
      u.volume = Math.min(1, prefs.get().volume);
      const est = Math.max(1200, text.length * 75);
      duck(est);
      markPlayed(est + 300);
      const done = () => resolve();
      u.onend = done;
      u.onerror = done;
      synth.speak(u);
      setTimeout(done, est * 2.5 + 2000); // safety: some browsers never fire onend
    };
    if (synth.getVoices().length) go();
    else {
      synth.addEventListener("voiceschanged", go, { once: true });
      setTimeout(go, 800);
    }
  });
}

export const narrator = new Narrator();

/** Whether this browser can speak the language at all (pre-recorded audio or a system voice). */
export function canNarrate(lang: Lang): boolean {
  if (manifest && Object.keys(manifest.lines).some((k) => k.startsWith(lang + "/"))) return true;
  return !!pickVoice(lang);
}

export function preloadNarration() {
  void loadManifest();
}
